import React, { useState, useMemo, useEffect } from "react";
import { Modal, ModalBody } from "reactstrap";
import Text from "components/Text";
import crossIcon from "assets/images/cross.svg";
import styles from "../../staff.module.scss";
import modalStyles from "./modals.module.scss";
const except = [1, 2];
const SelectOfficeActivationModal = ({
  isOpen,
  onClose,
  offices = [],
  onSubmit,
  selectedOfficeIds,
  radioEnabled,
  dontCheckAdmin,
  titlePage,
}) => {
  // Initialize states
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([...selectedOfficeIds]);
  const [selectAll, setSelectAll] = useState(false);

  // Ensure ID is always a STRING for consistent array checks
  const getOfficeId = (office) => office.id;

  // Reset search state when modal is opened or closed
  useEffect(() => {
    if (isOpen) {
      setSearch("");
    }
  }, [isOpen]);

  // Filtered list based on search text
  const filteredOffices = useMemo(() => {
    const s = (search || "").trim().toLowerCase();

    if (!offices?.length) return [];
    if (!s) return offices;

    return offices.filter((o) =>
      (o?.officeName || o?.name || "").toLowerCase().includes(s)
    );
  }, [search, offices]);

  // Sync "Select All" checkbox based on visible items + selected items
  useEffect(() => {
    if (filteredOffices.length > 0) {
      const visibleIds = filteredOffices
        .filter((res) => {
          if (!dontCheckAdmin && except.includes(res.otherDetails.type)) {
            return !res.otherDetails.isAdmin;
          }
          return true;
        })
        .map(getOfficeId);
      const allSelected =
        visibleIds.length > 0 &&
        visibleIds.every((id) => selected.includes(id));
      setSelectAll(allSelected);
    } else {
      setSelectAll(false);
    }
  }, [selected, filteredOffices]);

  // Handle individual office selection/deselection
  const toggleOffice = (officeId) => {
    setSelected((prev) =>
      prev.includes(officeId)
        ? prev.filter((id) => id !== officeId)
        : [...prev, officeId]
    );
  };

  // Handle Master "Select All" / "Deselect All" (visible items only)
  const handleSelectAll = () => {
    const visibleIds = filteredOffices
      .filter((res) => {
        if (!dontCheckAdmin && except.includes(res.otherDetails.type)) {
          return !res.otherDetails.isAdmin;
        }
        return true;
      })
      .map(getOfficeId);

    if (selectAll) {
      setSelected((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelected((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const handleContinue = () => {
    onSubmit([...selected]);
  };
  const inputType = useMemo(
    () => (radioEnabled ? "radio" : "checkbox"),
    [radioEnabled]
  );
  return (
    <Modal
      isOpen={!!isOpen}
      centered
      className="modal-dialog-centered modal-width-660"
      modalClassName="custom-modal"
    >
      <span className="close-btn" alt="close" onClick={onClose}>
        <img src={crossIcon} alt="close" />
      </span>
      <ModalBody>
        <Text size="25px" marginBottom="10px" weight="500" color="#111b45">
          <span className="modal-title-25">
            {titlePage || "Select Offices"}
          </span>
        </Text>

        {/* Search */}
        <div
          className={
            "search-box w-100 position-relative " + styles["search-box"]
          }
        >
          <input
            type="text"
            placeholder="Search office..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="staff_searchbar_padding"
          />

          <img
            src={require("assets/images/search-icon.svg").default}
            className="position-absolute icon_position"
            alt="icon"
          />
        </div>

        {/* Selection count (from image) */}
        {radioEnabled ? null : (
          <Text
            size="12px"
            marginBottom="24px"
            marginTop="24px"
            weight="400"
            color="rgb(111, 119, 136)"
          >
            {selected.length}{" "}
            {selected.length === 1 || !selected.length
              ? "Office Selected"
              : "Offices Selected"}
          </Text>
        )}

        {/* Office list */}
        <ul
          className={`${styles.checkbox_wrapper}  ${
            styles.check_scroll_wrapper
          }${radioEnabled ? " mt-4" : ""}`}
          id="officeScrollableDiv"
        >
          {filteredOffices?.length > 0 ? (
            <>
              {radioEnabled ? null : (
                <li
                  className={
                    "border-bottom w-100 " + modalStyles["radio-input"]
                  }
                >
                  <div className={`ch-checkbox ${styles.border_list}`}>
                    <label>
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        value="all"
                      />
                      <span>{`Select All`}</span>
                    </label>
                  </div>
                </li>
              )}
              {filteredOffices.map((office, i, alldata) => {
                const oid = getOfficeId(office);
                const checked = selected.includes(oid);

                const uniqueInputId = `office-activation-checkbox-${oid}`;
                const isAdmin = dontCheckAdmin
                  ? false
                  : office.otherDetails.isAdmin;
                const disabled = dontCheckAdmin
                  ? false
                  : except.includes(office.otherDetails.type) && isAdmin;
                return (
                  <li
                    key={uniqueInputId}
                    className={
                      alldata.length - 1 === i ? "w-100" : "border-bottom w-100"
                    }
                  >
                    <div className={`ch-${inputType} ${styles.border_list}`}>
                      <label
                        htmlFor={uniqueInputId}
                        className={modalStyles["radio-input"]}
                      >
                        <input
                          id={uniqueInputId}
                          type={inputType}
                          checked={checked}
                          disabled={disabled}
                          name={radioEnabled ? "single" : "multiple"}
                          // **ADDED VALUE PROP**
                          value={uniqueInputId}
                          onChange={() => {
                            if (radioEnabled) {
                              setSelected([oid]);
                            } else {
                              toggleOffice(oid);
                            }
                          }}
                        />
                        <span
                          className="d-flex flex-column"
                          style={{ pointerEvents: "none" }}
                        >
                          {office?.officeName || office?.name}
                          {disabled && (
                            <Text>Staff is Admin in this office</Text>
                          )}
                        </span>
                      </label>
                    </div>
                  </li>
                );
              })}
            </>
          ) : (
            <li>
              <div className="ch-checkbox text-center">
                <label>No offices found</label>
              </div>
            </li>
          )}
        </ul>

        {/* Buttons */}
        <div className="d-flex justify-content-start mt-4 pt-2 gap-2">
          <button
            disabled={selected.length === 0}
            onClick={handleContinue}
            className="button button-round button-shadow mr-md-4 w-sm-100"
          >
            Continue
          </button>
          <button
            onClick={onClose}
            className="button button-round button-border button-dark btn-mobile-link"
          >
            Cancel
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default SelectOfficeActivationModal;
